import { PageCanvas } from "@/components/page-canvas";
import { useState } from "react";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { dateTime } from "@/pages/jobs/types";

type Visit = {
  id: string;
  customer: { name: string };
  vehicle: { make: string; model: string; registration_number: string | null };
  starts_at: string;
  time_zone: string;
  currency: string;
  services: { name: string; description: string | null; quantity: number }[];
};

type Props = {
  jobs: Visit[];
  page: number;
  pages: number;
  history_path: string;
  report_path: string;
  back_path: string;
  customer_view: boolean;
};

export default function VehicleHistory({
  jobs,
  page,
  pages,
  history_path,
  report_path,
  back_path,
  customer_view,
}: Props) {
  const { t } = useTranslation();
  const { i18n, errors } = usePage().props;
  const [showPreviousErrors, setShowPreviousErrors] = useState(true);
  const clearPreviousErrors = () => setShowPreviousErrors(false);
  const content = (
    <PageCanvas>
      <Head title={t("vehicle_reports.title")} />
      <Link className="underline" href={back_path}>
        {t("common.back")}
      </Link>
      <h1 className="text-page font-display">{t("vehicle_reports.title")}</h1>
      <p className="text-muted-foreground text-sm">
        {t("vehicle_reports.scope")}
      </p>
      <p className="text-muted-foreground text-sm">
        {t("vehicle_reports.limits")}
      </p>
      {showPreviousErrors &&
        Object.entries(errors ?? {}).map(([field, message]) => (
          <p key={field} role="alert" className="text-destructive">
            {Array.isArray(message) ? message.join(" ") : message}
          </p>
        ))}
      {!jobs.length ? (
        <p>{t("vehicle_reports.empty")}</p>
      ) : (
        <form action={report_path} method="get" className="space-y-6">
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <Checkbox
                onCheckedChange={clearPreviousErrors}
                id="report-prices"
                aria-labelledby="report-prices-label"
                name="report[include_prices]"
                value="1"
              />
              <Label id="report-prices-label" htmlFor="report-prices">
                {t("vehicle_reports.include_prices")}
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                onCheckedChange={clearPreviousErrors}
                id="report-photos"
                aria-labelledby="report-photos-label"
                name="report[include_photos]"
                value="1"
              />
              <Label id="report-photos-label" htmlFor="report-photos">
                {t("vehicle_reports.include_photos")}
              </Label>
            </div>
          </div>
          <ol className="space-y-4">
            {jobs.map((job) => (
              <li key={job.id}>
                <Card>
                  <CardContent className="space-y-4 pt-6">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        onCheckedChange={clearPreviousErrors}
                        id={`visit-${job.id}`}
                        aria-labelledby={`visit-label-${job.id}`}
                        name="report[job_ids][]"
                        value={job.id}
                      />
                      <Label
                        id={`visit-label-${job.id}`}
                        htmlFor={`visit-${job.id}`}
                        className="min-w-0 flex-1 flex-wrap break-words"
                      >
                        {t("vehicle_reports.select")}:{" "}
                        {dateTime(job.starts_at, job.time_zone, i18n.locale)} ·{" "}
                        {job.customer.name}
                      </Label>
                    </div>
                    <p className="break-words font-medium">
                      {job.vehicle.make} {job.vehicle.model} ·{" "}
                      {job.vehicle.registration_number}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("vehicle_reports.currency")}: {job.currency}
                    </p>
                    <ul className="space-y-2">
                      {job.services.map((service, index) => (
                        <li key={index} className="break-words">
                          <p>
                            {service.quantity} × {service.name}
                          </p>
                          {service.description && (
                            <p className="text-muted-foreground text-sm">
                              {service.description}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ol>
          <Button type="submit">{t("vehicle_reports.download")}</Button>
        </form>
      )}
      <nav
        className="flex flex-wrap gap-4"
        aria-label={t("vehicle_reports.title")}
      >
        {page > 1 && (
          <Link className="underline" href={`${history_path}?page=${page - 1}`}>
            {t("customers.previous")}
          </Link>
        )}
        {page < pages && (
          <Link className="underline" href={`${history_path}?page=${page + 1}`}>
            {t("customers.next")}
          </Link>
        )}
      </nav>
    </PageCanvas>
  );
  return customer_view ? (
    <main id="main-content" tabIndex={-1}>
      {content}
    </main>
  ) : (
    <AppLayout breadcrumbs={[]} showWorkspaceNavigation={false}>
      {content}
    </AppLayout>
  );
}
